<template>
  <ChartBase
    :title="chartTitle"
    :date-range="dateRange"
    :subtitle="totalSalesUSD || '-'"
    :loading="loading"
    :error="error"
  >
    <template #actions>
      <v-btn-toggle v-model="selectedRange" mandatory dense class="mb-2">
        <v-btn small value="90">
          3mo
        </v-btn>
        <v-btn small value="120">
          4mo
        </v-btn>
        <v-btn small value="150">
          5mo
        </v-btn>
        <v-btn small value="180">
          6mo
        </v-btn>
      </v-btn-toggle>
    </template>
    <BarChart
      class="chart-admin-month-sales line-chart"
      :chart-options="chartOptions"
      :chart-data="chartData"
    />
  </ChartBase>
</template>

<script>
import AdminChartService from '@/services/AdminChartService'
import { SuperAdminBarChartOptions } from '@/constants/ChartOptions'
import ChartBase from '@/components/ChartBase.vue'

import _get from 'lodash/get'
import _min from 'lodash/min'
import _max from 'lodash/max'
import _sum from 'lodash/sum'

import { Bar as BarChart } from 'vue-chartjs/legacy'
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
} from 'chart.js'

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale)

export default {
  name: 'ChartAdminMonthSales',
  components: {
    ChartBase,
    BarChart,
  },
  data() {
    return {
      loading: true,
      totalSalesUSD: null,
      error: null,
      selectedRange: '90',
      chartData: {
        labels: [],
        datasets: [],
      },
      chartOptions: SuperAdminBarChartOptions,
    }
  },
  computed: {
    chartSlug() {
      const slugMap = {
        '90': 'admin-sales-3-months',
        '120': 'admin-sales-4-months',
        '150': 'admin-sales-5-months',
        '180': 'admin-sales-6-months',
      }
      return slugMap[this.selectedRange] || `admin-sales-${this.selectedRange}`
    },
    chartTitle() {
      return 'Daily Sales'
    },
    dateRange() {
      const rangeLabels = {
        '90': '3 Months',
        '120': '4 Months',
        '150': '5 Months',
        '180': '6 Months',
      }
      return `Last ${rangeLabels[this.selectedRange] ||
        this.selectedRange + ' Days'}`
    },
  },
  watch: {
    selectedRange() {
      this.loadChartData()
    },
  },
  async mounted() {
    await this.loadChartData()
  },
  methods: {
    async loadChartData() {
      this.error = null
      this.loading = true
      try {
        const { data: chartData } = await AdminChartService.getChart(
          this.chartSlug
        )
        this.chartData.labels = chartData.labels
        this.chartData.datasets = chartData.datasets
        this.setChartStepSize(chartData.datasets[0].data)
        this.setTotalSales(chartData.datasets[0].data)
      } catch (error) {
        let errorCode = _get(error, 'response.status')
        this.error =
          errorCode === 403
            ? 'Sorry, you do not have access to view this chart.'
            : _get(error, 'response.data.message', 'Could not load this chart')
      } finally {
        this.loading = false
      }
    },
    setChartStepSize(chartDataSet) {
      let min = _min(chartDataSet)
      let max = _max(chartDataSet)

      this.chartOptions.scales.y.suggestedMax = max
      let approxStepSize = (max - min) / 3
      let roundToNearest = 20
      this.chartOptions.scales.y.ticks.stepSize =
        Math.ceil(approxStepSize / roundToNearest) * roundToNearest
    }, // setChartStepSize
    setTotalSales(chartDataSet) {
      let sum = _sum(chartDataSet)
      this.totalSalesUSD = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(sum)
    }, // setTotalSales
  },
}
</script>

<style scoped>
.line-chart {
  height: 200px;
}
</style>
